// app/javascript/components/columnsAPI.js
// API слой для работы с метаданными колонок (ИСПРАВЛЕННАЯ ВЕРСИЯ)

// Кэш для метаданных колонок
let columnsMetadataCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 минут

// Кэш для chart groups
let chartGroupsCache = null;
let chartGroupsCacheTimestamp = null;

/**
 * Получает метаданные колонок из API с кэшированием
 * @returns {Promise<Array>} Массив объектов метаданных колонок
 */
export const fetchColumnsMetadata = async () => {
  // Проверяем кэш
  if (columnsMetadataCache && cacheTimestamp && 
      (Date.now() - cacheTimestamp) < CACHE_DURATION) {
    return columnsMetadataCache;
  }

  try {
    const response = await fetch('/api/statistic_columns/metadata', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Сохраняем в кэш
    columnsMetadataCache = data.columns || [];
    cacheTimestamp = Date.now();
    
    return columnsMetadataCache;
  } catch (error) {
    console.error('Error fetching columns metadata:', error);
    
    // Возвращаем кэш если есть, иначе пустой массив
    return columnsMetadataCache || [];
  }
};

/**
 * Получает chart groups из API с кэшированием
 * @returns {Promise<Array>} Массив объектов chart groups
 */
export const fetchChartGroups = async () => {
  // Проверяем кэш
  if (chartGroupsCache && chartGroupsCacheTimestamp && 
      (Date.now() - chartGroupsCacheTimestamp) < CACHE_DURATION) {
    return chartGroupsCache;
  }

  try {
    const response = await fetch('/api/statistic_columns/chart_groups', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Сохраняем в кэш
    chartGroupsCache = data.chart_groups || [];
    chartGroupsCacheTimestamp = Date.now();
    
    return chartGroupsCache;
  } catch (error) {
    console.error('Error fetching chart groups:', error);
    
    // Возвращаем кэш если есть, иначе пустой массив
    return chartGroupsCache || [];
  }
};

/**
 * Очищает кэш метаданных (например, после изменений в конструкторе)
 */
export const clearColumnsMetadataCache = () => {
  columnsMetadataCache = null;
  cacheTimestamp = null;
  chartGroupsCache = null;
  chartGroupsCacheTimestamp = null;
};

/**
 * Получает колонку по ключу из метаданных с безопасной обработкой
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Object|null} Объект колонки или null
 */
export const getColumnByKey = (key, metadata) => {
  if (!metadata || !Array.isArray(metadata)) {
    return null;
  }
  return metadata.find(col => col.key === key) || null;
};

/**
 * Проверяет, является ли колонка определенного типа
 * @param {string} key - Ключ колонки
 * @param {string} type - Тип колонки (base, calculated, percentage_ratio, percentage_attempts, error_category, chart_group)
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {boolean}
 */
export const isColumnType = (key, type, metadata) => {
  const column = getColumnByKey(key, metadata);
  return column?.column_type === type;
};

/**
 * Получает название (label) колонки
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string} Название колонки или сам ключ как fallback
 */
export const getColumnLabel = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  return column?.label || key;
};

/**
 * Получает описание колонки для тултипа
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string|null} Описание колонки или null
 */
export const getColumnDescription = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  return column?.description || null;
};

/**
 * Получает цвет колонки для графика
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string|null} Цвет в формате hex или null
 */
export const getColumnColor = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  return column?.chart_color || null;
};

/**
 * Проверяет, должна ли колонка отображаться на графике
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {boolean}
 */
export const shouldShowOnChart = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  return column?.show_on_chart === true;
};

/**
 * Проверяет, является ли колонка процентной
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {boolean}
 */
export const isPercentageColumn = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  return column?.is_percentage === true;
};

/**
 * Проверяет, является ли колонка chart group
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {boolean}
 */
export const isChartGroup = (key, metadata) => {
  return isColumnType(key, 'chart_group', metadata);
};

/**
 * Получает метрики группы для chart_group колонки
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив ключей метрик группы
 */
export const getGroupMetrics = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  if (!column || !column.is_chart_group) {
    return [];
  }
  return column.group_metrics || [];
};

/**
 * Получает цвета метрик группы для chart_group колонки
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Object} Объект с цветами метрик {metric_key: color}
 */
export const getGroupColors = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  if (!column || !column.is_chart_group) {
    return {};
  }
  return column.group_colors || {};
};

/**
 * Получает название группы для chart_group колонки
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string} Название группы
 */
export const getGroupName = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  if (!column || !column.is_chart_group) {
    return '';
  }
  return column.group_name || column.label || '';
};

/**
 * Проверяет, является ли колонка системной (date)
 * @param {string} key - Ключ колонки
 * @returns {boolean}
 */
export const isSystemColumn = (key) => {
  return key === 'date';
};

/**
 * Получает data_source колонки по её ключу
 * @param {string} key - Ключ колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string|null} Data source колонки или null
 */
export const getColumnDataSource = (key, metadata) => {
  const column = getColumnByKey(key, metadata);
  return column?.data_source || null;
};

/**
 * Проверяет, соответствует ли значение ключу колонки или её data_source
 * @param {string} targetValue - Значение для проверки (из конфигурации)
 * @param {string} columnKey - Ключ колонки для сравнения
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {boolean}
 */
export const isColumnMatch = (targetValue, columnKey, metadata) => {
  if (targetValue === columnKey) {
    return true;
  }
  
  const column = getColumnByKey(columnKey, metadata);
  return column?.data_source === targetValue;
};

/**
 * Резолвит ключ колонки в реальное поле данных (ИСПРАВЛЕННАЯ ВЕРСИЯ)
 * @param {string} columnKey - Ключ колонки (может быть ключом метаданных или полем данных)
 * @param {Object} statData - Данные статистики  
 * @param {Array} metadata - Метаданные колонок
 * @returns {number} Значение из данных
 */
const resolveColumnValue = (columnKey, statData, metadata) => {
  const column = getColumnByKey(columnKey, metadata);
  
  // ИСПРАВЛЕНИЕ: Если это calculated колонка, ВСЕГДА вычисляем её через calculateFormula
  // НЕ используем calculateColumnValue чтобы избежать циклической зависимости
  if (column && column.column_type === 'calculated') {
    return calculateFormula(statData, column.calculation_config, metadata);
  }
  
  // Для всех остальных типов колонок используем обычную логику
  
  // 1. Сначала проверяем, есть ли такое поле напрямую в данных
  if (statData.hasOwnProperty(columnKey)) {
    return statData[columnKey] || 0;
  }
  
  // 2. Если есть метаданные колонки и data_source
  if (column && column.data_source) {
    return statData[column.data_source] || 0;
  }
  
  // 3. Проверяем, может быть columnKey это data_source какой-то колонки
  const columnByDataSource = metadata.find(col => col.data_source === columnKey);
  if (columnByDataSource) {
    // Сначала пробуем найти по ключу колонки
    if (statData.hasOwnProperty(columnByDataSource.key)) {
      return statData[columnByDataSource.key] || 0;
    }
    // Иначе по data_source
    return statData[columnKey] || 0;
  }
  
  // 4. Последняя попытка - может это базовое поле?
  const baseFields = ['page_loads', 'sms_success', 'registration_success', 'registration_unfinished_sms', 'registration_unfinished_form'];
  if (baseFields.includes(columnKey)) {
    return statData[columnKey] || 0;
  }
  
  // Если ничего не найдено
  return 0;
};

/**
 * Вычисляет значение колонки на основе данных статистики (ИСПРАВЛЕННАЯ ВЕРСИЯ)
 * @param {string} key - Ключ колонки
 * @param {Object} statData - Объект с данными статистики
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {number} Вычисленное значение
 */
export const calculateColumnValue = (key, statData, metadata) => {
  const column = getColumnByKey(key, metadata);
  
  if (!column) {
    // Fallback для неизвестных колонок (например, date)
    return statData[key] || 0;
  }

  // Используем метод calculate_value из бэкенда (эмулируем логику фронтенда)
  switch (column.column_type) {
    case 'base':
      // Для base колонок используем data_source для получения данных
      let dataSource = column.data_source;
      
      // ВРЕМЕННЫЙ FALLBACK: если data_source пустой, угадываем по ключу колонки
      if (!dataSource) {
        const fallbackMapping = {
          'page_load': 'page_loads',
          'reg_success': 'registration_success', 
          'send_otpRequest_succes': 'sms_success'
        };
        dataSource = fallbackMapping[key] || key;
      }
      
      const value = statData[dataSource] || 0;
      return value;
      
    case 'calculated':
      // ИСПРАВЛЕНИЕ: ВСЕГДА вычисляем calculated колонки заново, игнорируя statData
      return calculateFormula(statData, column.calculation_config, metadata);
      
    case 'percentage_ratio':
      return calculatePercentageRatio(statData, column.calculation_config, metadata);
      
    case 'percentage_attempts':
      return calculatePercentageAttempts(statData, column.calculation_config, metadata);
      
    case 'error_category':
      return statData[key] || 0; // Ошибки уже считаются в StatisticsService
      
    case 'chart_group':
      return calculateChartGroup(statData, column, metadata);
      
    default:
      return statData[key] || 0;
  }
};

/**
 * Вычисляет значение chart_group колонки (сумма всех метрик группы)
 * @param {Object} statData - Данные статистики
 * @param {Object} column - Объект колонки chart_group
 * @param {Array} metadata - Метаданные колонок
 * @returns {number}
 */
const calculateChartGroup = (statData, column, metadata) => {
  if (!column.is_chart_group || !column.group_metrics) {
    return 0;
  }
  
  return column.group_metrics.reduce((sum, metricKey) => {
    return sum + resolveColumnValue(metricKey, statData, metadata);
  }, 0);
};

/**
 * Вычисляет формулу для calculated колонок (ИСПРАВЛЕННАЯ ВЕРСИЯ)
 * @param {Object} statData - Данные статистики
 * @param {Object} config - Конфигурация расчета
 * @param {Array} metadata - Метаданные колонок
 * @returns {number}
 */
const calculateFormula = (statData, config, metadata) => {
  if (!config) return 0;
  
  const formula = config.formula;
  const sourceColumns = config.source_columns || [];
  
  switch (formula) {
    case 'sum':
      return sourceColumns.reduce((sum, col) => sum + resolveColumnValue(col, statData, metadata), 0);
      
    case 'subtract':
      const base = resolveColumnValue(sourceColumns[0], statData, metadata);
      const subtractValue = resolveColumnValue(sourceColumns[1], statData, metadata);
      return Math.max(base - subtractValue, 0);
      
    case 'multiply':
      return sourceColumns.reduce((product, col) => product * resolveColumnValue(col, statData, metadata), 1);
      
    default:
      return 0;
  }
};

/**
 * Вычисляет процентное соотношение
 * @param {Object} statData - Данные статистики
 * @param {Object} config - Конфигурация расчета
 * @param {Array} metadata - Метаданные колонок
 * @returns {number}
 */
const calculatePercentageRatio = (statData, config, metadata) => {
  if (!config) return 0;
  
  const numeratorValue = resolveColumnValue(config.numerator, statData, metadata);
  const denominatorValue = resolveColumnValue(config.denominator, statData, metadata);
  
  if (denominatorValue === 0) return 0;
  
  return parseFloat((numeratorValue / denominatorValue * 100).toFixed(1));
};

/**
 * Вычисляет процент от попыток
 * @param {Object} statData - Данные статистики
 * @param {Object} config - Конфигурация расчета
 * @param {Array} metadata - Метаданные колонок
 * @returns {number}
 */
const calculatePercentageAttempts = (statData, config, metadata) => {
  if (!config) return 0;
  
  let targetValue = 0;
  let totalValue = 0;
  
  // Получаем target value
  targetValue = resolveColumnValue(config.target, statData, metadata);
  
  // Получаем total value
  totalValue = calculateTargetValue(statData, config, true, metadata);
  
  if (totalValue === 0) {
    return 0;
  }
  
  const result = parseFloat((targetValue / totalValue * 100).toFixed(1));
  return result;
};

/**
 * Вычисляет целевое значение для percentage_attempts
 * @param {Object} statData - Данные статистики
 * @param {Object} config - Конфигурация расчета
 * @param {boolean} isTotal - Вычислять total или target
 * @param {Array} metadata - Метаданные колонок
 * @returns {number}
 */
const calculateTargetValue = (statData, config, isTotal, metadata) => {
  const key = isTotal ? 'total_columns' : 'target_columns';
  const columns = config[key] || [];
  
  if (columns.length === 0) {
    const fallbackValue = resolveColumnValue(config.target, statData, metadata);
    return fallbackValue;
  }
  
  const formula = config[isTotal ? 'total_formula' : 'target_formula'] || 'sum';
  
  switch (formula) {
    case 'sum':
      const result = columns.reduce((sum, col) => {
        const value = resolveColumnValue(col, statData, metadata);
        return sum + value;
      }, 0);
      return result;
    default:
      return resolveColumnValue(config.target, statData, metadata);
  }
};

/**
 * Получает колонки, которые должны отображаться как пики на графике
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив колонок для графика (обычные + chart_group, исключая процентные)
 */
export const getChartColumns = (metadata) => {
  return metadata.filter(col => 
    col.show_on_chart === true && 
    col.is_percentage !== true
  );
};

/**
 * Получает обычные колонки для графика (исключая chart_group)
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив обычных колонок для графика
 */
export const getRegularChartColumns = (metadata) => {
  return metadata.filter(col => 
    col.show_on_chart === true && 
    col.is_percentage !== true &&
    col.column_type !== 'chart_group'
  );
};

/**
 * Получает chart_group колонки для графика
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив chart_group колонок
 */
export const getChartGroupColumns = (metadata) => {
  return metadata.filter(col => 
    col.show_on_chart === true && 
    col.column_type === 'chart_group'
  );
};

/**
 * Создает datasets для Chart.js с поддержкой stacked bar charts для chart_group
 * @param {Array} data - Массив данных статистики 
 * @param {Array} metadata - Метаданные колонок
 * @returns {Array} Массив datasets для Chart.js
 */
export const createChartDatasets = (data, metadata) => {
  if (!data || !metadata) return [];
  
  const datasets = [];
  const regularColumns = getRegularChartColumns(metadata);
  const chartGroupColumns = getChartGroupColumns(metadata);
  
  // Добавляем обычные колонки как line charts
  regularColumns.forEach(column => {
    datasets.push({
      label: column.label,
      data: data.map(item => calculateColumnValue(column.key, item, metadata)),
      borderColor: column.chart_color,
      backgroundColor: column.chart_color + '20', // добавляем прозрачность
      type: 'line',
      yAxisID: 'y',
      tension: 0.4
    });
  });
  
  // Добавляем chart_group колонки как stacked bar charts
  chartGroupColumns.forEach(groupColumn => {
    const groupMetrics = groupColumn.group_metrics || [];
    const groupColors = groupColumn.group_colors || {};
    
    groupMetrics.forEach((metricKey, index) => {
      const metricLabel = getColumnLabel(metricKey, metadata);
      const color = groupColors[metricKey] || generateFallbackColor(index);
      
      datasets.push({
        label: `${groupColumn.group_name || groupColumn.label}: ${metricLabel}`,
        data: data.map(item => resolveColumnValue(metricKey, item, metadata)),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        type: 'bar',
        stack: groupColumn.key, // Группируем по ключу chart_group
        yAxisID: 'y'
      });
    });
  });
  
  return datasets;
};

/**
 * Генерирует fallback цвет для метрик без заданного цвета
 * @param {number} index - Индекс метрики
 * @returns {string} Hex цвет
 */
const generateFallbackColor = (index) => {
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'
  ];
  return colors[index % colors.length];
};

/**
 * Вычисляет значения метрик для chart_group колонки
 * @param {string} groupKey - Ключ chart_group колонки
 * @param {Object} statData - Данные статистики
 * @param {Array} metadata - Метаданные колонок
 * @returns {Object} Объект с значениями метрик {metric_key: value}
 */
export const calculateGroupMetricValues = (groupKey, statData, metadata) => {
  
  const column = getColumnByKey(groupKey, metadata);
  if (!column || !column.is_chart_group) {
    console.log('Not a chart group or column not found');
    return {};
  }
  
  const result = {};
  const metrics = column.group_metrics || [];
  
  
  metrics.forEach(metricKey => {
    const value = resolveColumnValue(metricKey, statData, metadata);
    result[metricKey] = value;
  });
  
  return result;
};

/**
 * Получает процентные колонки, связанные с базовой колонкой для тултипов
 * @param {string} baseColumnKey - Ключ базовой колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив связанных процентных колонок
 */
export const getRelatedPercentageColumns = (baseColumnKey, metadata) => {
  return metadata.filter(col => {
    if (!col.is_percentage || !col.show_on_chart) {
      return false;
    }
    
    const config = col.calculation_config || {};
    
    // Показываем процентную колонку только если baseColumnKey является numerator или target
    if (col.column_type === 'percentage_ratio') {
      // Проверяем как по ключу колонки, так и по data_source
      return isColumnMatch(config.numerator, baseColumnKey, metadata);
    } else if (col.column_type === 'percentage_attempts') {
      if (config.target_is_calculated && config.target_columns) {
        // Если target вычисляется, проверяем target_columns
        return config.target_columns.some(targetCol => 
          isColumnMatch(targetCol, baseColumnKey, metadata)
        );
      } else {
        // Если target фиксированный, проверяем его
        return isColumnMatch(config.target, baseColumnKey, metadata);
      }
    }
    
    return false;
  });
};

/**
 * Получает текст источника для процентной колонки ("from SMS Success", "from Registration Attempts")
 * @param {Object} percentColumn - Объект процентной колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string}
 */
export const getPercentageSourceText = (percentColumn, metadata) => {
  const config = percentColumn.calculation_config || {};
  
  if (percentColumn.column_type === 'percentage_ratio') {
    // Для percentage_ratio показываем "from [denominator]"
    if (config.denominator) {
      const denomColumn = getColumnByKey(config.denominator, metadata);
      return `from ${denomColumn?.label || config.denominator}`;
    }
    return 'from Base';
  } else if (percentColumn.column_type === 'percentage_attempts') {
    // ИСПРАВЛЕНИЕ: Для percentage_attempts показываем "from [Total] Attempts"
    if (config.total_columns && config.total_columns.length > 0) {
      // Формируем название из total_columns
      const totalLabels = config.total_columns.map(col => {
        const column = getColumnByKey(col, metadata);
        return column?.label || col;
      });
      
      if (totalLabels.length === 1) {
        return `from ${totalLabels[0]} Attempts`;
      } else {
        // Если несколько колонок, показываем обобщенное название
        return `from ${totalLabels.join(' + ')} Attempts`;
      }
    }
    
    // Fallback к старой логике
    const targetLabel = percentColumn.label.replace(' Rate', '').replace(' %', '').replace(' Percentage', '');
    return `from ${targetLabel} Attempts`;
  }
  
  return 'from Base';
};

/**
 * Получает колонки, видимые по умолчанию (включая date)
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив ключей колонок, видимых по умолчанию
 */
export const getDefaultVisibleColumns = (metadata) => {
  const metadataVisible = metadata
    .filter(col => col.is_visible === true)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .map(col => col.key);
  
  // Date всегда видимая, остальные по настройкам
  return ['date', ...metadataVisible.filter(key => key !== 'date')];
};

/**
 * Получает порядок колонок по умолчанию (включая date)
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив ключей колонок в порядке display_order
 */
export const getDefaultColumnOrder = (metadata) => {
  const metadataColumns = metadata
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .map(col => col.key);
  
  // Date всегда первая, если её нет в метаданных
  return ['date', ...metadataColumns.filter(key => key !== 'date')];
};

/**
 * Проверяет, есть ли новые колонки в метаданных по сравнению с пользовательским порядком
 * @param {Array} userOrder - Пользовательский порядок колонок
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив новых колонок
 */
export const getNewColumns = (userOrder, metadata) => {
  const userColumns = new Set(userOrder);
  const metadataColumns = metadata.map(col => col.key);
  
  return metadataColumns.filter(colKey => !userColumns.has(colKey));
};

/**
 * Умное слияние пользовательского порядка с метаданными
 * @param {Array} userOrder - Пользовательский порядок колонок
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Объединенный порядок колонок
 */
export const mergeUserOrderWithMetadata = (userOrder, metadata) => {
  const validUserOrder = [];
  const usedColumns = new Set();
  
  // 1. Добавляем валидные колонки в пользовательском порядке
  userOrder.forEach(colId => {
    if (colId === 'date') {
      validUserOrder.push(colId);
      usedColumns.add(colId);
    } else if (metadata.some(col => col.key === colId)) {
      validUserOrder.push(colId);
      usedColumns.add(colId);
    }
  });
  
 // 2. Добавляем новые колонки из метаданных в базовом порядке
  const baseOrder = getDefaultColumnOrder(metadata);
  baseOrder.forEach(colId => {
    if (!usedColumns.has(colId)) {
      validUserOrder.push(colId);
      usedColumns.add(colId);
    }
  });
  
  return validUserOrder;
};